import { useEffect, useState } from "react";
import { Play, Pause } from "lucide-react";
import { useBranding } from "../lib/branding";
import { useLiveRadio } from "../lib/liveRadio";

export default function ListenLive() {
    const { logoUrl } = useBranding();
    const { meta, onAir, hasStream, isPlaying, togglePlay } = useLiveRadio();
    const [pulse, setPulse] = useState(0); // bumps every time the track changes

    // Re-pulse the fade animation when the track changes
    useEffect(() => {
        setPulse((n) => n + 1);
    }, [meta?.song, meta?.artist, meta?.album]);

    // Prefer live feed metadata; otherwise fall back to the admin-typed free-text line.
    const liveSong = (meta?.song || "").trim();
    const liveArtist = (meta?.artist || "").trim();
    const liveAlbum = (meta?.album || "").trim();
    const liveRaw = (meta?.raw_title || "").trim();
    const isLiveFeed = (meta?.source === "feed" || meta?.source === "push") && (liveSong || liveArtist);

    // Two-line layout: primary = song (or artist if no song), secondary = artist · album
    let primary;
    let secondary;
    if (isLiveFeed) {
        primary = liveSong || liveArtist || "Live Stream";
        const parts = [];
        if (liveSong && liveArtist) parts.push(liveArtist);
        if (liveAlbum) parts.push(liveAlbum);
        secondary = parts.join(" · ") || "Now playing";
    } else {
        const nowPlaying = liveRaw.trim();
        const [dj, ...rest] = nowPlaying.split(/\s+[·•—-]\s+/);
        primary = nowPlaying ? (dj || "Live Stream") : "DJ Kaywise";
        secondary = nowPlaying ? rest.join(" · ") || "Stay tuned" : "Afrobeats Street Mix";
    }

    const handleClick = togglePlay;

    return (
        <section
            data-testid="listen-live-widget"
            className="bg-streetz-paper border border-streetz-line p-6 md:p-8 flex flex-col gap-5"
        >
            <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                    <span className={`absolute inline-flex h-full w-full rounded-full ${onAir ? "bg-streetz-red" : "bg-streetz-muted"} opacity-75 ${onAir ? "animate-pulse-red" : ""}`} />
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${onAir ? "bg-streetz-red" : "bg-streetz-muted"}`} />
                </span>
                <span className="overline text-white">LISTEN LIVE</span>
                {isLiveFeed && (
                    <span data-testid="radio-meta-source-feed" className="ml-auto overline text-[9px] text-streetz-yellow border border-streetz-yellow px-1 py-[1px]">
                        LIVE METADATA
                    </span>
                )}
            </div>

            <div className="flex flex-col items-start gap-2">
                <img src={logoUrl} alt="MyStreetz Radio" className="brand-logo h-10 w-auto" />
                <div className="flex items-center gap-2">
                    <span className="font-display text-3xl text-white uppercase">RADIO</span>
                    {onAir ? (
                        <span data-testid="radio-on-air-badge" className="bg-streetz-red text-white px-2 py-1 overline text-[10px] animate-pulse-red">ON AIR</span>
                    ) : (
                        <span data-testid="radio-off-air-badge" className="bg-streetz-line text-streetz-muted px-2 py-1 overline text-[10px]">OFF AIR</span>
                    )}
                </div>
                <p className="overline text-streetz-muted">The Sound of the Streets</p>
            </div>

            <div className="flex items-center gap-4">
                <button
                    onClick={handleClick}
                    disabled={!hasStream}
                    data-testid="listen-live-play-button"
                    aria-label={isPlaying ? "Pause live radio" : "Play live radio"}
                    title={hasStream ? "" : "No stream URL configured (admin → Settings → Radio)"}
                    className={`h-16 w-16 rounded-full grid place-items-center transition-colors shrink-0 ${
                        hasStream
                            ? "bg-streetz-red text-white hover:bg-white hover:text-black"
                            : "bg-streetz-line text-streetz-muted cursor-not-allowed"
                    }`}
                >
                    {isPlaying ? <Pause className="w-7 h-7" /> : <Play className="w-7 h-7 ml-1" />}
                </button>
                <div className="flex items-end gap-1 h-10 flex-1">
                    {Array.from({ length: 22 }).map((_, i) => (
                        <span
                            key={`eq-bar-${i}`}
                            className="eq-bar"
                            style={{
                                height: `${30 + ((i * 13) % 70)}%`,
                                animationDelay: `${(i % 6) * 0.12}s`,
                                animationPlayState: isPlaying ? "running" : "paused",
                                opacity: isPlaying ? 1 : 0.4,
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className="border-t border-streetz-line pt-4">
                <div className="overline text-streetz-red">{onAir ? "LIVE NOW" : "NEXT UP"}</div>
                <div
                    key={`now-${pulse}`}
                    className="animate-fade-in"
                    data-testid="radio-now-playing-block"
                >
                    <div data-testid="radio-now-song" className="font-display uppercase text-xl text-white mt-1">{primary}</div>
                    <div data-testid="radio-now-artist-album" className="text-sm text-streetz-muted">{secondary || "Stay tuned"}</div>
                </div>
            </div>
        </section>
    );
}
